// permissionApi.js - API utilities for Shift Permission Management

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for permission API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Get current user ID
 * First tries to get from /tenant API, falls back to token payload if API fails
 */
export const getUserId = async () => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  // Try API endpoint first
  try {
    const res = await fetch(`${API_BASE_URL}/tenant`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (res.ok) {
      const data = await res.json();
      const userId = data.user_id || data.employee_id || data.id;
      if (userId) {
        return userId;
      }
    }
  } catch (error) {
    console.warn('API call to /tenant failed, trying token payload:', error);
  }

  // Fallback: Get user ID from token payload
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const payload = tokens.idToken.payload;
      // Extract user ID from token payload
      const userId = payload.sub || payload['cognito:username'] || payload.username;
      if (userId) {
        console.log('Using user ID from token payload:', userId);
        return userId;
      }
    }
  } catch (error) {
    console.error('Error getting user ID from token:', error);
  }

  console.error('Could not retrieve user ID from API or token');
  return null;
};

/**
 * Get assigned site IDs for a user
 * GET /dashboard/user/site/v2?user_id={userId}
 */
export const getAssignedSiteIds = async (userId) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('ID token not found. Please log in again.');
  }

  try {
    const url = `${API_BASE_URL}/dashboard/user/site/v2?user_id=${userId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await response.json();
      if (data.site_details != null) {
        const siteDetails = data.site_details;
        const siteId = siteDetails.site_id;
        return [siteId];
      } else {
        throw new Error('Site details not found in response');
      }
    } else {
      const error = new Error(`Failed to fetch assigned site IDs: ${response.status}`);
      error.status = response.status;
      error.endpoint = '/dashboard/user/site/v2';
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_shift_permission',
        `Shift Permission API error: ${response.status} for /dashboard/user/site/v2`,
        error,
        {
          endpoint: '/dashboard/user/site/v2',
          method: 'GET',
          status: response.status,
          userId: userId,
        }
      ).catch(logError => {
        console.warn('[shiftpermissionApi] Failed to log error:', logError);
      });
      
      throw error;
    }
  } catch (error) {
    console.error('Error fetching assigned site IDs:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_shift_permission',
        `Shift Permission API network/fetch error for /dashboard/user/site/v2`,
        error,
        {
          endpoint: '/dashboard/user/site/v2',
          method: 'GET',
          errorType: error.name || 'NetworkError',
          userId: userId,
        }
      ).catch(logError => {
        console.warn('[shiftpermissionApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Fetch user site details
 * GET /dashboard/user/site/v2?user_id={userId}
 */
export const fetchUserSiteDetails = async (userId) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  try {
    const url = `${API_BASE_URL}/dashboard/user/site/v2?user_id=${userId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await response.json();
      if (data.site_details != null) {
        const siteInfo = data.site_details;
        const shiftInfo = data.shift_details || {};

        return {
          site_name: siteInfo.site_name,
          site_id: siteInfo.site_id,
          address: siteInfo.address,
          geofence_details: siteInfo.geofence_details,
          activity_by: siteInfo.activity_by,
          activity_timestamp: siteInfo.activity_timestamp,
          shift_start_time: shiftInfo.shift_start_time,
          shift_end_time: shiftInfo.shift_end_time,
          shift_start_timestamp: shiftInfo.shift_start_timestamp,
          shift_end_timestamp: shiftInfo.shift_end_timestamp,
          shift_id: shiftInfo.shift_id,
        };
      } else {
        return null;
      }
    } else {
      const error = new Error(`Failed to fetch site details: ${response.status}`);
      error.status = response.status;
      error.endpoint = '/dashboard/user/site/v2';
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_shift_permission',
        `Shift Permission API error: ${response.status} for /dashboard/user/site/v2`,
        error,
        {
          endpoint: '/dashboard/user/site/v2',
          method: 'GET',
          status: response.status,
          userId: userId,
        }
      ).catch(logError => {
        console.warn('[shiftpermissionApi] Failed to log error:', logError);
      });
      
      throw error;
    }
  } catch (error) {
    console.error('Error fetching user site details:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_shift_permission',
        `Shift Permission API network/fetch error for /dashboard/user/site/v2`,
        error,
        {
          endpoint: '/dashboard/user/site/v2',
          method: 'GET',
          errorType: error.name || 'NetworkError',
          userId: userId,
        }
      ).catch(logError => {
        console.warn('[shiftpermissionApi] Failed to log error:', logError);
      });
    }
    
    return null;
  }
};

/**
 * Fetch shift assignments for a site and date
 * GET /shiftroaster?tenant_site_id={siteId}&date={formattedDate}
 */
export const fetchShiftAssignments = async (siteId, date) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token not found');
  }

  // Format date as YYYY-MM-DD
  const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const url = `${API_BASE_URL}/shiftroaster?tenant_site_id=${siteId}&date=${formattedDate}`;

  try {
    console.log("Request URL:", url);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(`Failed to fetch shift assignments: ${response.status}`);
      error.status = response.status;
      error.endpoint = '/shiftroaster';
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_shift_permission',
        `Shift Permission API error: ${response.status} for /shiftroaster`,
        error,
        {
          endpoint: '/shiftroaster',
          method: 'GET',
          status: response.status,
          siteId: siteId,
          date: formattedDate,
        }
      ).catch(logError => {
        console.warn('[shiftpermissionApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching shift assignments:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_shift_permission',
        `Shift Permission API network/fetch error for /shiftroaster`,
        error,
        {
          endpoint: '/shiftroaster',
          method: 'GET',
          errorType: error.name || 'NetworkError',
          siteId: siteId,
        }
      ).catch(logError => {
        console.warn('[shiftpermissionApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get profile photo URL
 * GET /user/profile-picture?user_id={userId}
 */
export const getProfilePhoto = async (userId) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required.');
  }

  try {
    const url = `${API_BASE_URL}/user/profile-picture?user_id=${userId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return '';
    }

    const data = await response.json();
    return data.profile_image_url || data.image_url || '';
  } catch (error) {
    console.warn(`Profile photo not available for user ${userId}:`, error);
    return '';
  }
};

/**
 * Create permission
 * POST /permissions/create
 */
export const createPermission = async (permissionData) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const url = `${API_BASE_URL}/permissions/create`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(permissionData),
    });

    if (response.status === 201) {
      const data = await response.json();
      return data;
    } else if (response.status === 400) {
      throw new Error('Invalid JSON in request body.');
    } else {
      const errorText = await response.text();
      const error = new Error(
        `Failed to create permission: ${response.status} - ${errorText}`
      );
      error.status = response.status;
      error.endpoint = '/permissions/create';
      error.responseData = errorText;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_shift_permission',
        `Shift Permission API error: ${response.status} for /permissions/create`,
        error,
        {
          endpoint: '/permissions/create',
          method: 'POST',
          status: response.status,
        }
      ).catch(logError => {
        console.warn('[shiftpermissionApi] Failed to log error:', logError);
      });
      
      throw error;
    }
  } catch (error) {
    console.error('Error creating permission:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_shift_permission',
        `Shift Permission API network/fetch error for /permissions/create`,
        error,
        {
          endpoint: '/permissions/create',
          method: 'POST',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[shiftpermissionApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get permissions given by the current supervisor
 * GET /permissions/get-my-given-permissions
 */
export const getMyGivenPermissions = async () => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const url = `${API_BASE_URL}/permissions/get-my-given-permissions`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 200) {
      const data = await response.json();
      return data;
    } else {
      const errorText = await response.text();
      const error = new Error(
        `Error fetching given permissions: ${response.status} - ${errorText}`
      );
      error.status = response.status;
      error.endpoint = '/permissions/get-my-given-permissions';
      error.responseData = errorText;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_shift_permission',
        `Shift Permission API error: ${response.status} for /permissions/get-my-given-permissions`,
        error,
        {
          endpoint: '/permissions/get-my-given-permissions',
          method: 'GET',
          status: response.status,
        }
      ).catch(logError => {
        console.warn('[shiftpermissionApi] Failed to log error:', logError);
      });
      
      throw error;
    }
  } catch (error) {
    console.error('Error fetching permissions:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_shift_permission',
        `Shift Permission API network/fetch error for /permissions/get-my-given-permissions`,
        error,
        {
          endpoint: '/permissions/get-my-given-permissions',
          method: 'GET',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[shiftpermissionApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

